import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Handshake, ShieldCheck, Rocket, CheckCircle2, Building2, Users, BarChart3, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const ROADMAP = [
  { phase: "Phase 1 — Today", status: "live", items: [
    "Verified-only marketplace (no fake deals)",
    "AI-powered Deal Intelligence scoring",
    "Dealer Rating & Transparency on every offer",
    "Lead routing to verified broker",
  ]},
  { phase: "Phase 2 — Next 90 days", status: "soon", items: [
    "Dealer Self-Service Portal — submit lease offers for admin approval",
    "Dealer Inventory feed import (CSV + Marketcheck adapter)",
    "Lead-quality SLA dashboard for partner dealers",
    "Co-branded landing pages with partner dealer logos",
  ]},
  { phase: "Phase 3 — Q3 2026", status: "planned", items: [
    "Live API integration with major DMS systems (CDK, Reynolds, Dealertrack)",
    "Auto-syndicated dealer specials with disclaimer verification",
    "Multi-rooftop dealer-group analytics rollup",
    "Optional pay-per-lead pricing tier",
  ]},
];

const BENEFITS = [
  { icon: Users, title: "Pre-qualified buyers", body: "Every lead comes with vehicle preference, budget, credit range, and timeline already collected." },
  { icon: ShieldCheck, title: "Verified marketplace", body: "Your deals go up alongside other verified offers — no spammy listings to compete against." },
  { icon: BarChart3, title: "Conversion analytics", body: "See which of your offers get viewed, saved, and converted to inquiries." },
  { icon: Building2, title: "Multi-rooftop ready", body: "Dealer groups can roll up multiple rooftops into a single dashboard view." },
];

export default function DealerPortal() {
  const [form, setForm] = useState({
    business_name: "", dealer_principal_name: "", email: "", phone: "",
    rooftop_count: 1, primary_brands: "", location_state: "", location_city: "",
    website: "", monthly_volume_estimate: "", interest: "submit_deals", notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/dealer-applications", {
        ...form,
        rooftop_count: parseInt(form.rooftop_count) || 1,
        monthly_volume_estimate: parseInt(form.monthly_volume_estimate) || 0,
      });
      toast.success("Application received — we'll be in touch within 48 hours.");
      setSubmitted(true);
    } catch (err) {
      toast.error("Couldn't submit — please try again or email partners@555auto.com");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14" data-testid="dealer-portal-page">
      <p className="label-mono mb-3 flex items-center gap-2"><Handshake className="w-3 h-3 text-silver" /> Dealer Partnerships</p>
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter mb-4">Become a 555 partner dealer.</h1>
      <p className="text-lg text-gray-400 max-w-2xl mb-12">Pre-qualified lease buyers, transparent listings, and a verified-only marketplace your store can stand behind.</p>

      {/* Benefits */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
        {BENEFITS.map((b) => (
          <div key={b.title} className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6">
            <b.icon className="w-5 h-5 text-silver mb-4" />
            <div className="font-display text-lg mb-2">{b.title}</div>
            <p className="text-sm text-gray-400 leading-relaxed">{b.body}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* Roadmap */}
        <div className="lg:col-span-7">
          <p className="label-mono mb-3 flex items-center gap-2"><Rocket className="w-3 h-3" /> Roadmap</p>
          <h2 className="font-display text-3xl font-light tracking-tight mb-7">What you're partnering into.</h2>
          <div className="space-y-5">
            {ROADMAP.map((r) => (
              <div key={r.phase} className={`rounded-2xl p-6 border ${r.status === "live" ? "border-emerald-500/30 bg-emerald-500/5" : r.status === "soon" ? "border-yellow-500/20 bg-yellow-500/5" : "border-white/10 bg-white/[0.02]"}`} data-testid={`dealer-roadmap-${r.status}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display text-lg">{r.phase}</div>
                  <span className={`text-[10px] tracking-widest font-bold px-2 py-1 rounded-full ${r.status === "live" ? "bg-emerald-500/20 text-emerald-300" : r.status === "soon" ? "bg-yellow-500/20 text-yellow-300" : "bg-white/10 text-gray-400"}`}>{r.status.toUpperCase()}</span>
                </div>
                <ul className="space-y-2">
                  {r.items.map((it, i) => (
                    <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                      <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${r.status === "live" ? "text-emerald-400" : "text-gray-500"}`} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-6 flex items-center gap-2"><FileSpreadsheet className="w-3 h-3" /> Today: admin-curated bulk CSV imports. Phase 2: dealer self-serve portal.</p>
        </div>

        {/* Application */}
        <aside className="lg:col-span-5">
          <div className="sticky top-24 glass rounded-2xl p-7" data-testid="dealer-application-form">
            <p className="label-mono mb-2">Apply</p>
            <h3 className="font-display text-2xl mb-1">Get on the partner waitlist</h3>
            <p className="text-xs text-gray-500 mb-6">No commitment. We'll reach out with onboarding details when your phase opens.</p>

            {submitted ? (
              <div className="py-12 text-center" data-testid="dealer-application-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
                <div className="font-display text-xl mb-1">Application received.</div>
                <p className="text-sm text-gray-400">We'll be in touch within 48 hours.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <Input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Dealership name" className="bg-[#0A0A0E] border-[#1A1A24] h-11 text-white" data-testid="dealer-form-business" />
                <Input required value={form.dealer_principal_name} onChange={(e) => setForm({ ...form, dealer_principal_name: e.target.value })} placeholder="Your name" className="bg-[#0A0A0E] border-[#1A1A24] h-11 text-white" data-testid="dealer-form-principal" />
                <div className="grid grid-cols-2 gap-3">
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email" className="bg-[#0A0A0E] border-[#1A1A24] h-11 text-white" data-testid="dealer-form-email" />
                  <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="bg-[#0A0A0E] border-[#1A1A24] h-11 text-white" data-testid="dealer-form-phone" />
                </div>
                <Input required value={form.primary_brands} onChange={(e) => setForm({ ...form, primary_brands: e.target.value })} placeholder="Primary brands (e.g. BMW, MINI)" className="bg-[#0A0A0E] border-[#1A1A24] h-11 text-white" data-testid="dealer-form-brands" />
                <div className="grid grid-cols-2 gap-3">
                  <Input required value={form.location_city} onChange={(e) => setForm({ ...form, location_city: e.target.value })} placeholder="City" className="bg-[#0A0A0E] border-[#1A1A24] h-11 text-white" data-testid="dealer-form-city" />
                  <Input required value={form.location_state} onChange={(e) => setForm({ ...form, location_state: e.target.value })} placeholder="State" className="bg-[#0A0A0E] border-[#1A1A24] h-11 text-white" maxLength={2} data-testid="dealer-form-state" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input type="number" min="1" value={form.rooftop_count} onChange={(e) => setForm({ ...form, rooftop_count: e.target.value })} placeholder="# of rooftops" className="bg-[#0A0A0E] border-[#1A1A24] h-11 text-white" data-testid="dealer-form-rooftops" />
                  <Input type="number" min="0" value={form.monthly_volume_estimate} onChange={(e) => setForm({ ...form, monthly_volume_estimate: e.target.value })} placeholder="Monthly volume (est.)" className="bg-[#0A0A0E] border-[#1A1A24] h-11 text-white" data-testid="dealer-form-volume" />
                </div>
                <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className="w-full bg-[#0A0A0E] border border-[#1A1A24] h-11 rounded-md text-white px-3 text-sm" data-testid="dealer-form-interest">
                  <option value="submit_deals">Submit lease deals for approval</option>
                  <option value="buy_leads">Buy qualified buyer leads</option>
                  <option value="both">Both</option>
                </select>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything else we should know?" rows={3} className="w-full bg-[#0A0A0E] border border-[#1A1A24] rounded-md text-white p-3 text-sm" data-testid="dealer-form-notes" />
                <button type="submit" disabled={busy} className="w-full silver-gradient text-[#050507] font-bold py-3.5 rounded-full disabled:opacity-50" data-testid="dealer-form-submit">
                  {busy ? "Submitting…" : "Submit application"}
                </button>
                <p className="text-[10px] text-gray-500 text-center">We never sell your data. Replies typically within 2 business days.</p>
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
